import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  type ButtonProps,
  type DialogActionsProps,
  type DialogContentProps,
  type DialogContentTextProps,
  type DialogProps,
  type DialogTitleProps,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Typography from "@mui/material/Typography";
import type { SxProps, Theme } from "@mui/material/styles";
import type { ReactNode } from "react";
import AppButton from "../form/AppButton";

const dialogPaperSx: SxProps<Theme> = {
  borderRadius: 1.25,
  border: 1,
  borderColor: "divider",
  overflow: "hidden",
  boxShadow: (theme) =>
    theme.palette.mode === "dark"
      ? "0 12px 40px rgba(0,0,0,0.5)"
      : "0 12px 40px rgba(15, 23, 42, 0.12)",
};

const fullscreenPaperSx: SxProps<Theme> = {
  borderRadius: 0,
  border: 0,
};

function mergePaperSx(fullScreen: boolean | undefined, userSx?: SxProps<Theme>): SxProps<Theme> {
  const base = fullScreen ? fullscreenPaperSx : dialogPaperSx;
  if (!userSx) return base;
  return [base, userSx] as SxProps<Theme>;
}

/** Compact Win11 dialog shell — tight title / body / actions rhythm. */
export function AppDialog({ slotProps, fullScreen: fullScreenProp, ...rest }: DialogProps) {
  // Phones get a full-screen sheet unless the caller decided explicitly.
  const isPhone = useMediaQuery(useTheme().breakpoints.down("sm"));
  const fullScreen = fullScreenProp ?? isPhone;
  const paperSlot = slotProps?.paper;
  const paperObj =
    paperSlot && typeof paperSlot === "object" && !Array.isArray(paperSlot) ? paperSlot : {};
  const userPaperSx = "sx" in paperObj ? (paperObj.sx as SxProps<Theme>) : undefined;

  return (
    <Dialog
      scroll="paper"
      fullScreen={fullScreen}
      slotProps={{
        ...slotProps,
        paper: {
          elevation: 0,
          ...paperObj,
          sx: mergePaperSx(fullScreen, userPaperSx),
        },
      }}
      {...rest}
    />
  );
}

type AppDialogTitleProps = DialogTitleProps & {
  endAdornment?: ReactNode;
};

export function AppDialogTitle({ children, endAdornment, sx, ...rest }: AppDialogTitleProps) {
  return (
    <DialogTitle
      sx={[
        {
          py: 1.25,
          px: 2,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
      {...rest}
    >
      {typeof children === "string" ? (
        <Typography variant="subtitle1" fontWeight={650} color="text.primary" component="span">
          {children}
        </Typography>
      ) : (
        children
      )}
      {endAdornment}
    </DialogTitle>
  );
}

export function AppDialogContent({ sx, ...rest }: DialogContentProps) {
  return (
    <DialogContent
      sx={[
        {
          bgcolor: "background.paper",
          px: 2,
          py: 1.75,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          // Undo MUI DialogContent first-child top padding bump
          "&:first-of-type": { pt: 1.75 },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
      {...rest}
    />
  );
}

export function AppDialogDescription({ sx, ...rest }: DialogContentTextProps) {
  return (
    <DialogContentText
      component="div"
      sx={[
        { color: "text.secondary", mb: 0, fontSize: "0.8125rem", lineHeight: 1.45 },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
      {...rest}
    />
  );
}

export function AppDialogActions({ sx, ...rest }: DialogActionsProps) {
  const theme = useTheme();
  return (
    <DialogActions
      sx={[
        {
          px: 2,
          py: 1.25,
          borderTop: 1,
          borderColor: "divider",
          gap: 0.75,
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.03)"
              : "rgba(15, 23, 42, 0.02)",
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
      {...rest}
    />
  );
}

export function AppDialogCancelButton({ children, loading: _loading, ...rest }: ButtonProps) {
  return (
    <AppButton variant="outlined" color="primary" size="small" busyIgnore {...rest}>
      {children}
    </AppButton>
  );
}

export function AppDialogPrimaryButton({ children, loading, ...rest }: ButtonProps) {
  return (
    <AppButton
      variant="contained"
      color="primary"
      size="small"
      loading={loading ?? undefined}
      {...rest}
    >
      {children}
    </AppButton>
  );
}
